// ─────────────────────────────────────────────────────────────────
// Glass-i — Current Stock PIVOT view.
//
// Item description (rows) × sheet size W×H (columns), cells summing
// Qty / SQM / SQFT. Every figure comes from the server aggregate
// (GET /api/erp/inventory/stock-pivot) — this component renders DB
// values only, never fabricated numbers. It is server-paginated on
// rows and the backend hard-caps the size columns (top-N by volume +
// a folded "Other sizes" column), so the matrix stays a fixed width
// and the payload stays small no matter how much stock the tenant
// holds.
//
// The measure toggle (Qty / SQM / SQFT) switches which summed measure
// fills the cells instantly — all three are returned per cell, so no
// refetch is needed. Area (SQM/SQFT) is shown at exactly 3 dp per
// CLAUDE.md rule 5A; Qty (sheets / pieces / lengths) stays a count.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Empty, Pagination, Segmented, Spin, Tooltip, message } from 'antd';
import { TableOutlined } from '@ant-design/icons';

import { inventoryApi } from './inventoryApi';
import './StockPivot.css';

// ── formatting (Indian grouping; DB values only) ──────────────────
const NF0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const NF3 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const NFQ = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 3 });

// Area (m² / ft²) — strict 3-dp (rule 5A).
function fmtArea(v) {
  const n = Number(v);
  return Number.isFinite(n) ? NF3.format(n) : '—';
}
// Qty — whole counts show whole; fractional (linear metres) up to 3 dp.
function fmtQty(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? NF0.format(n) : NFQ.format(n);
}
function fmtMeasure(v, measure) {
  return measure === 'qty' ? fmtQty(v) : fmtArea(v);
}
function fmtMm(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}
function errMessage(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message)
    || (e && e.message) || fallback;
}

const MEASURE_OPTIONS = [
  { label: 'Qty',  value: 'qty' },
  { label: 'SQM',  value: 'sqmt' },
  { label: 'SQFT', value: 'sqft' }
];
const MEASURE_UNIT = { qty: 'units', sqmt: 'm²', sqft: 'ft²' };
const PAGE_SIZE_OPTIONS = ['25', '50', '100'];

// One measured cell — the selected measure only, so a wide matrix stays
// scannable. Empty (no stock at that item × size) → muted dash.
function cellValue(cell, measure) {
  if (!cell) return null;
  return measure === 'qty' ? Number(cell.qty || 0) : Number(cell[measure] || 0);
}

export default function StockPivotView({ filters }) {
  const [measure, setMeasure] = useState('qty');
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  // Serialise the shared filter object so an unchanged filter set does
  // not retrigger the fetch on every parent render.
  const filterKey = useMemo(() => JSON.stringify(filters || {}), [filters]);

  // Any filter change resets to page 1.
  useEffect(() => { setPage(1); }, [filterKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const f = filters || {};
      const params = { limit: pageSize, offset: (page - 1) * pageSize };
      if (f.search)        params.search       = f.search;
      if (f.item_type)     params.item_type    = f.item_type;
      if (f.warehouse_id)  params.warehouse_id = f.warehouse_id;
      if (f.thickness_mm != null && f.thickness_mm !== '') params.thickness_mm = f.thickness_mm;

      const out = await inventoryApi.getStockPivot(params);
      setData(out || null);
    } catch (e) {
      message.error({ content: errMessage(e, 'Failed to load the stock pivot.'), key: 'spv-load' });
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const columns    = (data && data.columns) || [];
  const rows       = (data && data.rows) || [];
  const colTotals  = (data && data.colTotals) || {};
  const grandTotal = (data && data.grandTotal) || { qty: 0, sqmt: 0, sqft: 0 };
  const pageInfo   = (data && data.page) || {};
  const rowTotalCount = Number(pageInfo.rowTotalCount || 0);
  const unit = MEASURE_UNIT[measure];

  const isEmpty = !loading && rows.length === 0;

  return (
    <Card
      className="spv-card"
      bordered={false}
      bodyStyle={{ padding: 0 }}
      title={(
        <span className="spv-title">
          <span className="spv-title__ic"><TableOutlined /></span>
          Stock pivot
          <span className="spv-title__sub">item × size ({unit})</span>
        </span>
      )}
      extra={(
        <div className="spv-toolbar">
          <Segmented
            size="small"
            value={measure}
            onChange={(v) => setMeasure(v)}
            options={MEASURE_OPTIONS}
          />
        </div>
      )}
    >
      <Spin spinning={loading}>
        {isEmpty ? (
          <div className="spv-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No stock matches these filters."
            />
          </div>
        ) : (
          <>
            <div className="spv-scroll">
              <table className="spv-pivot">
                <thead>
                  <tr>
                    <th className="spv-rl">Item</th>
                    {columns.map((c) => (
                      <th key={c.key} className={c.isOther ? 'spv-oth' : ''}>
                        {c.isOther
                          ? <Tooltip title="All sizes beyond the top-ranked columns, combined"><span>{c.label}</span></Tooltip>
                          : c.label}
                      </th>
                    ))}
                    <th className="spv-tot">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.itemKey}>
                      <td className="spv-rl">
                        <Tooltip title={row.itemDesc}>
                          <span className="spv-rl__name">{row.itemDesc}</span>
                        </Tooltip>
                        {row.thicknessMm != null
                          ? <span className="spv-rl__thk">{fmtMm(row.thicknessMm)} mm</span>
                          : null}
                      </td>
                      {columns.map((c) => {
                        const v = cellValue(row.cells ? row.cells[c.key] : null, measure);
                        return (
                          <td key={c.key} className={v === null ? 'spv-empty-cell' : `spv-val${c.isOther ? ' spv-oth' : ''}`}>
                            {v === null ? '–' : fmtMeasure(v, measure)}
                          </td>
                        );
                      })}
                      <td className="spv-val spv-tcol">{fmtMeasure(row.total ? row.total[measure === 'qty' ? 'qty' : measure] : 0, measure)}</td>
                    </tr>
                  ))}

                  {/* Column totals — over ALL items, not just this page */}
                  <tr className="spv-coltot">
                    <td className="spv-rl">All items</td>
                    {columns.map((c) => {
                      const t = colTotals[c.key];
                      const v = t ? (measure === 'qty' ? t.qty : t[measure]) : 0;
                      return <td key={c.key} className="spv-val">{fmtMeasure(v, measure)}</td>;
                    })}
                    <td className="spv-val spv-grand">
                      {fmtMeasure(measure === 'qty' ? grandTotal.qty : grandTotal[measure], measure)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="spv-footer">
              <span className="spv-footnote">
                Rows are items ({rowTotalCount.toLocaleString('en-IN')} total); columns are the top sheet
                sizes by volume{columns.some((c) => c.isOther) ? ', with the long tail folded into “Other sizes”' : ''}.
                Column totals span all items. Values are live stock balances.
              </span>
              <Pagination
                size="small"
                current={page}
                pageSize={pageSize}
                total={rowTotalCount}
                showSizeChanger
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                showTotal={(count, range) =>
                  count === 0 ? 'No items' : `${range[0]}–${range[1]} of ${count} items`}
                onChange={(nextPage, nextSize) => {
                  if (nextSize !== pageSize) { setPageSize(nextSize); setPage(1); }
                  else { setPage(nextPage); }
                }}
              />
            </div>
          </>
        )}
      </Spin>
    </Card>
  );
}
